import React from 'react';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'interactive';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  children?: React.ReactNode;
}

export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'md',
      children,
      className = '',
      onClick,
      ...props
    },
    ref
  ) => {
    // Map variant to CSS class
    const variantClasses: Record<string, string> = {
      default: 'card',
      elevated: 'card-elevated',
      interactive: 'card-interactive',
    };

    // Map padding to CSS class (override default card padding)
    const paddingClasses: Record<string, string> = {
      none: 'p-0',
      sm: 'p-4',
      md: 'p-6 max-md:p-4', // Responsive: p-6 on desktop, p-4 on mobile
      lg: 'p-8 max-md:p-6',
    };

    // Combine classes
    const cardClasses = [
      variantClasses[variant],
      paddingClasses[padding], // Always apply padding classes
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        ref={ref}
        className={cardClasses}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick(e as any);
                }
              }
            : undefined
        }
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

export const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ title, subtitle, action, children, className = '', ...props }, ref) => {
    const headerClasses = ['card-header', className].filter(Boolean).join(' ');

    return (
      <div ref={ref} className={headerClasses} {...props}>
        {children ? (
          children
        ) : (
          <>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {title && (
                  <h3 className="text-lg font-semibold text-secondary-900">
                    {title}
                  </h3>
                )}
                {subtitle && (
                  <p className="text-sm text-secondary-600 mt-1">{subtitle}</p>
                )}
              </div>
              {action && <div className="ml-4">{action}</div>}
            </div>
          </>
        )}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

export const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ children, className = '', ...props }, ref) => {
    const contentClasses = ['card-content', className].filter(Boolean).join(' ');

    return (
      <div ref={ref} className={contentClasses} {...props}>
        {children}
      </div>
    );
  }
);

CardContent.displayName = 'CardContent';

export const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ children, className = '', ...props }, ref) => {
    const footerClasses = ['card-footer', className].filter(Boolean).join(' ');

    return (
      <div ref={ref} className={footerClasses} {...props}>
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';
